import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getUnits, getLessonsByUnit } from '@/lib/db'
import type { Unit, Lesson } from '@/types'
import {
  BookOpen,
  Search,
  Filter,
  ChevronLeft,
  Loader2,
  Play
} from 'lucide-react'

export default function LessonsList() {
  const [searchTerm, setSearchTerm] = useState('')
  const [unitFilter, setUnitFilter] = useState('all')
  const [units, setUnits] = useState<Unit[]>([])
  const [lessons, setLessons] = useState<(Lesson & { unit?: Unit })[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      const { data: unitsData } = await getUnits()
      if (unitsData) {
        setUnits(unitsData)
        // Fetch lessons for all units
        const allLessons: (Lesson & { unit?: Unit })[] = []
        for (const unit of unitsData) {
          const { data: unitLessons } = await getLessonsByUnit(unit.id)
          if (unitLessons) {
            allLessons.push(...unitLessons.map(l => ({ ...l, unit })))
          }
        }
        setLessons(allLessons)
      }
      setLoading(false)
    }

    loadData()
  }, [])

  const filteredLessons = lessons.filter(lesson => {
    const title = lesson.title_es || lesson.title
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesUnit = unitFilter === 'all' || lesson.unit_id === unitFilter
    return matchesSearch && matchesUnit
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/teacher/content" className="p-2 hover:bg-muted rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Lecciones</h1>
          <p className="text-muted-foreground">
            {`${lessons.length} lecciones disponibles (solo lectura)`}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            <option value="all">Todas las unidades</option>
            {units.map(u => (
              <option key={u.id} value={u.id}>{u.title}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de lecciones */}
      <div className="grid gap-3">
        {filteredLessons.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">No se encontraron lecciones</p>
          </div>
        ) : (
          filteredLessons.map((lesson) => {
            const title = lesson.title_es || lesson.title
            const description = lesson.description_es || lesson.description

            return (
              <div
                key={lesson.id}
                className="bg-card rounded-xl border p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{title}</h3>
                    {description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">{description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {lesson.unit && (
                        <span className="px-2 py-0.5 bg-muted rounded">{lesson.unit.title}</span>
                      )}
                      <span>Lección {lesson.order_num}</span>
                    </div>
                  </div>
                  <Link
                    to={`/teacher/preview/lesson/${lesson.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors shrink-0"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Ver
                  </Link>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
